import axios from 'axios';
import { UpdateUserDto } from '../views/user/interfaces/UpdateUser';
import attachAuthToken from './axiosConfig';

const usersApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/users`,
  withCredentials: true,
});

attachAuthToken(usersApi);

const getErrorMessage = (error: unknown, fallback: string) => {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message;

    if (Array.isArray(message)) {
      return message.join(', ');
    }

    if (typeof message === 'string') {
      return message;
    }

    return fallback;
  }

  return 'Erro desconhecido';
};

export interface User {
  id: string;
  username: string;
  email: string;
  fullName: string;
  /** Papel principal — exibição e compatibilidade (at-81). */
  userType: string;
  /**
   * Todos os perfis do usuário (at-81). Ausente nas respostas que ainda não o devolvem —
   * quem exibe cai no `userType`, que é exatamente o comportamento anterior.
   */
  papeis?: string[];
  isAdmin: boolean;
  createdAt: string;
  updatedAt: string;
  codigoVoluntario?: string | null;
  codigoRegional?: string | null;
  deletedAt?: string | null;
}

export interface PaginatedUsersResponse {
  total: number;
  page: number;
  limit: number;
  users: User[];
}

/** Tri-estado do filtro de status da tela de usuários (at-61). */
export type UserStatusFilter = 'ATIVOS' | 'INATIVOS' | 'TODOS';

export interface FindUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  /** Legado: a tela usa `status` desde a at-61. Mantido porque o backend ainda aceita. */
  includeDeleted?: boolean;
  status?: UserStatusFilter;
  /** Tipos de usuário separados por vírgula (enum, não rótulo de exibição). */
  userTypes?: string;
  /** YYYY-MM-DD, inclusive. */
  createdFrom?: string;
  /** YYYY-MM-DD, inclusive — o backend estende para o fim do dia. */
  createdTo?: string;
}

export interface SearchCollaboratorsParams extends FindUsersParams {
  fullName: string;
}

export interface ChangePasswordPayload {
  newPassword: string;
  confirmPassword: string;
}

export interface MessageResponse {
  message: string;
}

export const getUsers = async (
  params: FindUsersParams = { page: 1, limit: 10 }
): Promise<PaginatedUsersResponse> => {
  try {
    const response = await usersApi.get('/', {
      params,
    });

    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao buscar usuários'));
  }
};

export const getUserById = async (id: string): Promise<User> => {
  try {
    const response = await usersApi.get(`/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao buscar usuário'));
  }
};

export const deleteUser = async (id: string): Promise<MessageResponse> => {
  try {
    const response = await usersApi.delete(`/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao desativar usuário'));
  }
};

export const restoreUser = async (id: string): Promise<MessageResponse> => {
  try {
    const response = await usersApi.patch(`/${id}/restore`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao reativar usuário'));
  }
};

export const updateUser = async (
  id: string,
  updateUserDto: UpdateUserDto
): Promise<User> => {
  try {
    const response = await usersApi.patch(`/${id}`, updateUserDto);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao atualizar usuário'));
  }
};

export const getCollaborators = async (
  params: FindUsersParams = { page: 1, limit: 10 }
): Promise<PaginatedUsersResponse> => {
  try {
    const response = await usersApi.get('/collaborators', {
      params,
    });

    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao buscar colaboradores'));
  }
};

/**
 * at-129 — o autocomplete de menção (@). Só id, nome e username de usuários ativos, até 10.
 *
 * ⚠️ Sem catch: quem chama decide o que mostrar. O campo de menção avisa e segue sem sugerir —
 * a menção é acessório do comentário, nunca motivo para travar o texto.
 */
export const buscarMencionaveis = async (
  q: string,
): Promise<Array<{ id: string; fullName: string | null; username: string | null }>> => {
  const { data } = await usersApi.get('/mencionaveis', { params: { q } });
  return data;
};

export const searchCollaborators = async ({
  fullName,
  page = 1,
  limit = 10,
}: SearchCollaboratorsParams): Promise<PaginatedUsersResponse> => {
  try {
    const response = await usersApi.get('/searchCollaborator', {
      params: {
        fullName,
        page,
        limit,
      },
    });

    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Erro ao buscar colaboradores'));
  }
};

// A redefinição de senha do fluxo "esqueci minha senha" usa resetPassword (authApi) —
// a rota PATCH /users/:id/change-password é estritamente do próprio usuário autenticado.
